import { Timestamp } from 'firebase/firestore';
import type { IShipmentContactData } from './Party';
import type { CompletionTrigger } from '../../utils/shipmentLock';

//import { ShipmentMilestones } from "./TemporaryShipment";

export type ConsolidationType = 'standalone' | 'master' | 'house';

export interface IHouseRef {
  id: string;
  idNumber: string;
  hbl?: string | null;
  hawb?: string | null;
  status?: string;
  consignee?: string | null;
}

// Entidad principal de Shipment
export interface Shipment {
  id: string;
  liberationNumber: string;
  shipmentDate?: Date | Timestamp | null;
  requestedPickupDate?: Date | Timestamp;
  requestedDeliveryDate?: Date | Timestamp;
  shipmentNumber?: string;
  status: ShipmentStatus;
  transportMode: TransportMode;
  shipmentType: ShipmentType | "";
  incoterms?: string;
  initialFreight?: { value: number; currency: string };
  finalFreight?: { value: number; currency: string };
  totalCharges?: { value: number; currency: string };
  incoterm?: string;
  incotermLocation?: string;
  origin?: string;
  destination?: string;
  originLocationCode?: string;
  originLocationName?: string;
  destinationLocationCode?: string;
  destinationLocationName?: string;
  customerReference?: string;
  customerReferenceNumber?: string[];
  project?: string;
  rateCategory?: string;
  deliveryMode?: string;
  lastUpdate: Date | Timestamp;
  shipmentCompletedDate?: Date | Timestamp;
  isShipmentCompleted?: boolean;
  lifecycleChange?: { by?: string; trigger?: CompletionTrigger };
  statusHistory?: Array<{
    type: 'COMPLETED' | 'REACTIVATED';
    at: Date | Timestamp;
    by: string;
    trigger: CompletionTrigger | null;
    previousStatus: string | null;
  }>;
  completedAt?: Date | Timestamp;
  completedBy?: string;
  completionTrigger?: CompletionTrigger | null;
  reactivatedAt?: Date | Timestamp;
  reactivatedBy?: string;
  activeDaysInMonth?: Array<{ month: string; days: number }>;
  leadTimePTP_ATA?: number | null;
  referenceThree?: string;
  referenceFour?: string;
  referenceThreeValue?: string[];
  referenceFourValue?: string[];
  relatedReference?: string;
  relatedReferenceNumber?: string[];
  idNumberReference?: string;
  idNumber?: string;
  internationalCarrier?: string;
  pl3?: string; // Para el 3PL
  booking?: string;
  mbl?: string;
  hbl?: string;
  mawb?: string;
  hawb?: string;
  disabledReference?: 'mbl' | 'booking' | 'container' | null;
  serviceType?: string;
  shippingLine?: string;
  alliance?: string;
  invoice?: string;
  purchaseOrder?: string;
  priority?: string;
  comments?: string;
  vessel?: string;
  eta?: Date | Timestamp | null;
  freeDays?: number | null;
  storageFreeDays?: number | null;
  milestones?: ShipmentMilestones;
  updatedBy?: {
    id: string;
    name: string;
    lastName: string;
  } | null;
  assignedTo?: string;
  isImported?: boolean; // Track if shipment was created through import
  createdBy: string;
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
  isTracking?: boolean;
  trackingStartedAt?: Date | Timestamp;
  trackingStatus?: 'active' | 'paused' | 'error';
  lastTrackingUpdate?: Date | Timestamp;
  trackingProvider?: string;
  containersDeleted?: string[]; // Números de contenedores eliminados manualmente (para evitar recreación por tracking)
  isIMOCargo?: boolean; // Indica si la carga es peligrosa/IMO (requiere documentos especiales)
  // Parties - Partes involucradas en el embarque
  shipper?: IShipmentContactData | null;
  consignee?: IShipmentContactData | null;
  notify?: IShipmentContactData | null;
  pickup?: IShipmentContactData | null;
  delivery?: IShipmentContactData | null;
  customer?: IShipmentContactData | null;
  vendor?: IShipmentContactData | null;
  // Insurance
  policyNumber?: string;
  // Consolidation (Master/House)
  consolidationType?: ConsolidationType;
  masterShipmentId?: string | null;
  masterShipmentNumber?: string | null;
  houseCount?: number;
  houses?: IHouseRef[];
}

export enum ShipmentStatus {
  BOOKING_REQUEST = 'BOOKING_REQUEST',
  PICKED_UP = 'PICKED_UP',
  DEPARTED = 'DEPARTED',
  ARRIVED = 'ARRIVED',
  CUSTOMS = 'CUSTOMS',
  CLEARED = 'CLEARED',
  DELIVERED = 'DELIVERED',
  COMPLETED = 'COMPLETED'
}

export enum TransportMode {
  SEA = 'Ocean Transport',
  AIR = 'AIR',
  ROAD = 'ROAD',
  RAIL = 'RAIL'
}

// Agregar después de TransportMode
export enum ShipmentType {
  FCL = 'Contenedor completo',
  LCL = 'Contenedor parcial'
}

// También necesitamos asegurarnos que ShipmentMilestones tenga los campos opcionales
export interface ShipmentMilestones {
  requestedPickupDate?: Date | Timestamp | string | null;
  requestedDeliveryDate?: Date | Timestamp | string | null;
  requestedTransitTime?: string;
  pickupDate?: Date | Timestamp | string | null;
  portOfLoading?: string;
  portOfDischarge?: string;
  portOfReceipt?: string;
  vessel?: string;
  voyage?: string;
  flag?: string;
  etd?: Date | Timestamp | string | null;
  eta?: Date | Timestamp | string | null;
  atd?: Date | Timestamp | string | null;
  ata?: Date | Timestamp | string | null;
  lastMilePickupLocation?: string;
  lastMileCarrier?: string;
  deliveryDate?: Date | Timestamp | string | null;
  placeOfReceipt?: string;
  placeOfDelivery?: string;
  estimatedPickupDate?: Date | Timestamp | string | null;
  actualPickupDate?: Date | Timestamp | string | null;
  estimatedDeliveryDate?: Date | Timestamp | string | null;
  actualDeliveryDate?: Date | Timestamp | null;
  airportOfDeparture?: string;
  airportOfArrival?: string;
  flight?: string;
  plate?: string;
  freeStorageDate?: Date | Timestamp | null;
  freeRetentionDate?: Date | Timestamp | null;
  loadingDate?: Date | Timestamp | null;
  portReturnDate?: Date | Timestamp | null;
  portPickupDate?: Date | Timestamp | null;
  lastMileDeliveryPlace?: string;
}